// Shell — persistent app shell that mounts ONCE at the router level.
//
// Contains Sidebar, top header (search trigger + profile menu), mobile menu,
// and the global ⌘K command palette. Below it we render `<Outlet />` which
// swaps as the URL changes — but the shell itself, its sidebar scroll
// position, its open dropdowns, its keyboard shortcuts — all stay mounted.
//
// This kills the whole class of remount bugs (Sidebar scroll reset, focus
// loss, animation restarts, etc.) at the source.
import { useEffect, useRef, useState } from "react";
import { Link, Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  ChevronDown, LogOut, Menu, Search, Settings as SettingsIcon,
  Shield, Sparkles, UserCircle2, X,
} from "lucide-react";

import { useAuth } from "../context/AuthContext";
import { useNavPrefs } from "../hooks/useNavPrefs";
import { useEffectiveAccess } from "../hooks/useEffectiveAccess";
import GlobalSearchPalette from "./GlobalSearchPalette";
import CoPilotDrawer from "./CoPilotDrawer";
import { ThemeToggle } from "../context/ThemeContext";
import NotificationBell from "./NotificationBell";
import PresenceTracker from "./PresenceTracker";
import FloatingNotepad from "./FloatingNotepad";
import { TrainingProvider } from "./training/TrainingContext";
import TrainingButton from "./training/TrainingButton";
import { SquadProvider } from "./squad/SquadContext";
import SquadButton from "./squad/SquadButton";
import SquadAvatar from "./squad/SquadAvatar";
import ScrollToTop from "./ScrollToTop";
import Sidebar from "./Sidebar";
import ShellMobileMenu from "./ShellMobileMenu";
import { getNavForUser } from "../config/nav";
import { resolvePageId } from "../lib/access";
import AccessDenied from "../pages/access/AccessDenied";
import { AreaLevelContext } from "../hooks/useAreaLevel";


export default function Shell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [gcmdOpen, setGcmdOpen] = useState(false);
  const profileRef = useRef(null);

  // Global ⌘K / Ctrl+K → open command palette. `/` opens it too (unless the
  // user is already typing in an input).
  useEffect(() => {
    const onKey = (e) => {
      const isEditable = /^(input|textarea|select)$/i.test(e.target?.tagName || "")
        || e.target?.isContentEditable;
      if ((e.metaKey || e.ctrlKey) && (e.key === "k" || e.key === "K")) {
        e.preventDefault();
        setGcmdOpen((v) => !v);
        return;
      }
      if (e.key === "/" && !isEditable && !gcmdOpen) {
        e.preventDefault();
        setGcmdOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [gcmdOpen]);

  const { hideRoadmap } = useNavPrefs();
  const access = useEffectiveAccess();
  const navItems = getNavForUser(user, { hideRoadmap, access });

  // Client-side access gate — mirror of the server require_access(page_id).
  // Only denies once access has loaded and the user is genuinely restricted.
  const currentPageId = resolvePageId(location.pathname);
  const accessDenied =
    access && access.unrestricted === false && currentPageId
      ? !(access.pages || []).includes(currentPageId)
      : false;
  const areaCtx = { unrestricted: !!access?.unrestricted, levels: access ? access.levels : {}, pageId: currentPageId };
  const viewOnlyHere = !areaCtx.unrestricted && currentPageId && !accessDenied && areaCtx.levels?.[currentPageId] === "view";
  const page = accessDenied ? <AccessDenied pageId={currentPageId} /> : (
    <AreaLevelContext.Provider value={areaCtx}>
      {viewOnlyHere && <div className="mb-3 inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-medium bg-sky-500/10 text-sky-200 border border-sky-400/30" data-testid="view-only-banner">View only · you can read this area but not change it</div>}
      <Outlet />
    </AreaLevelContext.Provider>
  );

  // Close open menus on route change.
  useEffect(() => {
    setMenuOpen(false);
    setProfileOpen(false);
  }, [location.pathname]);

  // Escape closes any open menu.
  useEffect(() => {
    if (!menuOpen && !profileOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") { setMenuOpen(false); setProfileOpen(false); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [menuOpen, profileOpen]);

  // Outside-click closes the profile menu.
  useEffect(() => {
    if (!profileOpen) return;
    const onClick = (e) => {
      if (profileRef.current && !profileRef.current.contains(e.target)) {
        setProfileOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [profileOpen]);

  if (new URLSearchParams(location.search).get("embed") === "1") {
    return <TrainingProvider><SquadProvider><div className="min-h-screen bg-[#0B1120]" data-testid="embed-shell">{page}</div></SquadProvider></TrainingProvider>;
  }

  return (
    <TrainingProvider>
    <SquadProvider>
    <div className="min-h-screen text-white flex" style={{ background: "transparent" }}>
      <Sidebar navItems={navItems} />

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 backdrop-blur-xl bg-[#0F1A30]/90 border-b border-white/5">
          <div className="px-4 sm:px-6 h-14 flex items-center gap-3">
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg text-slate-200 hover:bg-white/5 border border-white/5"
              aria-label={menuOpen ? "Close menu" : "Open menu"}
              aria-expanded={menuOpen}
              aria-controls="mobile-nav-panel"
              data-testid="mobile-menu-toggle"
            >
              {menuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>

            <Link
              to="/home"
              className="lg:hidden flex items-center gap-2 group"
              data-testid="brand-link-mobile"
            >
              <img
                src="/logo-talmia-ii.svg"
                alt="Talmia II"
                className="w-8 h-8 rounded-lg object-cover shadow-lg shadow-orange-500/20"
              />
              <span className="hidden sm:inline md:hidden font-display text-base font-bold tracking-tight">
                Talmia<span className="text-orange-400">II</span>
              </span>
            </Link>

            <button
              type="button"
              onClick={() => setGcmdOpen(true)}
              className="hidden md:inline-flex items-center gap-2 pl-3 pr-2 py-1.5 rounded-lg border border-white/10 bg-white/[0.03] text-slate-300 hover:text-white hover:border-white/20 hover:bg-white/[0.06] transition-colors text-sm md:min-w-[220px] lg:min-w-[380px]"
              data-testid="global-search-trigger"
              aria-label="Open command palette"
            >
              <Search className="w-3.5 h-3.5 text-orange-400" />
              <span className="text-slate-400 whitespace-nowrap truncate">Co-Pilot<span className="hidden lg:inline"> — search anything…</span></span>
              <span className="ml-auto flex items-center gap-1 font-mono text-[10px] text-slate-500">
                <kbd className="px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/10">⌘</kbd>
                <kbd className="px-1.5 py-0.5 rounded bg-white/[0.06] border border-white/10">K</kbd>
              </span>
            </button>

            <div className="ml-auto flex items-center gap-2 sm:gap-3">
              <TrainingButton />
              <SquadButton />
              <span className="hidden sm:contents"><FloatingNotepad /></span>
              <NotificationBell />
              <span className="hidden sm:contents"><PresenceTracker /></span>
              <ThemeToggle />

              <button
                type="button"
                onClick={() => setGcmdOpen(true)}
                className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-lg border border-white/10 text-slate-300 hover:text-white hover:bg-white/[0.06]"
                data-testid="global-search-trigger-mobile"
                aria-label="Open command palette"
              >
                <Search className="w-4 h-4 text-orange-400" />
              </button>

              <div className="relative" ref={profileRef}>
                <button
                  type="button"
                  onClick={() => setProfileOpen((v) => !v)}
                  aria-haspopup="menu"
                  aria-expanded={profileOpen}
                  data-testid="profile-trigger"
                  className={`flex items-center gap-2 pr-2 pl-1 py-1 rounded-full transition-colors border ${
                    profileOpen ? "bg-white/10 border-white/20" : "border-transparent hover:bg-white/5"
                  }`}
                >
                  <SquadAvatar name={user?.name} url={user?.avatar_url} size={32} />
                  <div className="text-left hidden lg:block max-w-[140px]">
                    <div className="text-[13px] font-medium leading-tight text-white whitespace-nowrap truncate" data-testid="user-name">{user?.name}</div>
                    <div className="text-[10.5px] text-slate-400 leading-tight font-mono">
                      {user?.role === "admin" ? "Admin" : "User"}
                    </div>
                  </div>
                  <ChevronDown className={`w-3.5 h-3.5 text-slate-400 transition-transform ${profileOpen ? "rotate-180" : ""}`} />
                </button>

                {profileOpen && (
                  <div
                    role="menu"
                    data-testid="profile-menu"
                    className="absolute right-0 mt-2 min-w-[240px] rounded-lg border border-white/10 bg-[#0F1420]/95 backdrop-blur-xl shadow-2xl overflow-hidden"
                  >
                    <div className="px-4 py-3 border-b border-white/[0.06] bg-white/[0.02]">
                      <div className="text-[13px] font-semibold text-white leading-tight">{user?.name}</div>
                      <div className="text-[11px] text-slate-400 font-mono leading-tight mt-0.5">{user?.email}</div>
                      <div className="mt-1.5 inline-flex items-center gap-1 text-[9.5px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded"
                        style={{ background: user?.role === "admin" ? "rgba(255,104,32,.15)" : "rgba(148,163,184,.12)",
                                 color: user?.role === "admin" ? "#ff6820" : "#94a3b8",
                                 border: `1px solid ${user?.role === "admin" ? "rgba(255,104,32,.35)" : "rgba(148,163,184,.2)"}` }}>
                        <Shield className="w-2.5 h-2.5" />
                        {user?.role === "admin" ? "Admin" : "User"}
                      </div>
                    </div>
                    <div className="py-1">
                      <Link
                        to="/profile"
                        role="menuitem"
                        onClick={() => setProfileOpen(false)}
                        data-testid="profile-menu-profile"
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
                      >
                        <UserCircle2 className="w-4 h-4 text-indigo-300" />
                        Profile settings
                      </Link>
                      <button
                        type="button"
                        role="menuitem"
                        disabled
                        data-testid="profile-menu-preferences"
                        className="w-full flex items-center gap-2.5 px-4 py-2 text-sm text-slate-500 cursor-not-allowed"
                      >
                        <SettingsIcon className="w-4 h-4 text-slate-500" />
                        Preferences
                        <span className="ml-auto text-[9px] font-mono uppercase tracking-widest text-slate-600">Soon</span>
                      </button>
                      <a
                        href="/profile"
                        onClick={(e) => { e.preventDefault(); setProfileOpen(false); navigate("/profile"); }}
                        role="menuitem"
                        data-testid="profile-menu-universal-key"
                        className="flex items-center gap-2.5 px-4 py-2 text-sm text-slate-300 hover:bg-white/5 hover:text-white transition-colors"
                      >
                        <Sparkles className="w-4 h-4 text-orange-400" />
                        Universal Key
                      </a>
                    </div>
                    <div className="border-t border-white/[0.06]">
                      <button
                        type="button"
                        onClick={async () => { setProfileOpen(false); await logout(); navigate("/login"); }}
                        role="menuitem"
                        data-testid="profile-menu-logout"
                        className="w-full flex items-center gap-2.5 px-4 py-2.5 text-sm text-red-300 hover:bg-red-500/10 hover:text-red-200 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Sign out
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {menuOpen && (
            <ShellMobileMenu
              user={user}
              navItems={navItems}
              onClose={() => setMenuOpen(false)}
            />
          )}
        </header>

        {/* Outlet — this is what swaps on route change. Everything above stays mounted. */}
        {page}
      </div>

      <CoPilotDrawer open={gcmdOpen} onClose={() => setGcmdOpen(false)} />
      <ScrollToTop />
    </div>
    </SquadProvider>
    </TrainingProvider>
  );
}
